import { Button } from "../../../components/ui/button";
import { StatsCard } from "../../../componentsV2/dashboardV2/StatsCard";
import { RevenueChart } from "../../../componentsV2/dashboardV2/RevenueChart";
import { PerformanceItem } from "../../../componentsV2/dashboardV2/PerformanceItem";
import { Select, SelectProps } from "antd";
import { useMemo, useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../../util/axiosConfig"
import { useCurrency } from "../../../hooks/useCurrency";
import { Link, useNavigate } from "react-router-dom";


const PERIOD: SelectProps['options'] = [
  {
    label: 'Today',
    value: 'd',
  },
  {
    label: 'Current Week',
    value: '7d',
  },
  {
    label: 'Last Week',
    value: '1w',
  },
  {
    label: 'Last Months',
    value: '1m',
  },
  {
    label: 'Last 3 Months',
    value: '3m',
  },
  {
    label: 'Last 6 Months',
    value: '6m',
  },
  {
    label: 'Last year',
    value: '1yr',
  },
]
export default function DashboardPage() {
  const navigateTo = useNavigate();
  const [activePeriod, setActivePeriod] = useState('3m'); // 3m, 6m or 1yr
  const {formatLargeNumber} = useCurrency();
  const { data, isLoading } = useSWR(`dashboard/admin/?period=${activePeriod}`, fetcher);

  const dashboard = useMemo(() => {
    return data?.data?.dashboard;
  }, [data]);

  const stats = useMemo(() => {
    return data?.data?.stats;
  }, [data]);

  const fleetOwnerPerformance = useMemo(() => {
    return data?.data?.fleetOwnerPerformance;
  }, [data]);

  const RevenueChartPoints = useMemo(() => {
    return data?.data?.revenueGraph?.points;
  }, [data]);

  const handleChange = (value: string) => {
    setActivePeriod(value);
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Pending Requests"
          value={dashboard?.pendingRenterRequests || 0}
        >
          <div className="text-gray-500 space-y-4 px-6">
            {dashboard?.awaitingResponse && dashboard?.awaitingResponse > 0 && (
              <div className="text-sm flex items-center justify-between">
                <span className="font-medium">Request actions</span>
                <span className="font-medium"><strong className="text-secondary">{dashboard?.awaitingResponse}</strong> Awaiting Response</span>
              </div>
            )}
            <div className="flex items-center gap-0">
              <Button className="w-2/6 rounded-none text-white" onClick={() => navigateTo('/admin/requests')}>
                Assign
              </Button>
              <Button className="text-gray-700 flex-1 rounded-none bg-gray-400 hover:bg-gray-400 hover:opacity-80" onClick={() => navigateTo('/admin/requests')}>
                Auto-assign
              </Button>
            </div>
          </div>
        </StatsCard>

        <StatsCard
          title="Active Rentals"
          value={dashboard?.activeRentals || 0}
          showImage
        >
          <div className="text-gray-500 space-y-2">
            {/* <div className="text-sm flex items-center justify-end px-6">
              <span className="font-medium"><strong className="text-secondary">8</strong> Location services available</span>
            </div> */}
            <hr className="border-primary" />
            <Button variant="ghost" className="bg-transparent text-primary w-full font-semibold" onClick={() => navigateTo('/admin/requests/?status=ACTIVE')}>
              See details
            </Button>
          </div>
        </StatsCard>

        <StatsCard
          title="Unresolved Complaints"
          value={dashboard?.unresolvedComplaints || 0}
          titleTextColor="text-secondary"
        >
          <div className="text-gray-500 space-y-2">
            <div className="px-6">
              <h3 className="text-base text-secondary font-semibold mb-4">Payments Recorded</h3>
              <Link
                to="/admin/payout-requests"
                className="text-primary text-2xl font-bold"
              >
                {stats?.totalPayoutRequest || 0}
                <span className="text-sm ml-2">Payouts</span>
              </Link>
            </div>
          </div>
        </StatsCard>
      </section>

      <section className="bg-white">
        {/* Time Filter */}
        <div className="flex justify-end items-center p-6">
          <span className="font-medium text-sm text-gray-500 mr-4">Showing for:</span>
          <Select
            size="middle"
            defaultValue={activePeriod}
            onChange={handleChange}
            style={{ width: 200 }}
            options={PERIOD}
          />
          {/* <Button className="text-white rounded-none">Last 3 Months</Button> */}
        </div>
        <hr className="border-primary" />
        {/* Revenue Chart */}
        <div className="flex flex-col md:flex-row gap-6 p-6 pb-12">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-primary mb-8">Revenue</h2>
            <RevenueChart data={RevenueChartPoints} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-primary">
                  Total Revenue
                </h3>
                <p className="text-3xl font-bold">
                  {/* 108M */}
                  {formatLargeNumber(stats?.totalRevenue || 0)}
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-primary">
                  Total Rental Requests
                </h3>
                <p className="text-3xl font-bold">
                  {stats?.totalRentalRequests || 0}
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-primary">
                  Total Rentals Completed
                </h3>
                <p className="text-3xl font-bold">
                  {stats?.totalRentalsCompleted || 0}
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-primary">
                  Total Unattended
                </h3>
                <p className="text-3xl font-bold flex">
                  {stats?.totalUnattendedRequests || 0}
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-2 text-secondary p-0 font-medium mt-auto"
                    onClick={() => navigateTo('/admin/requests')}
                  >
                    Resolve
                  </Button>
                </p>
              </div>
            </div>
          </div>

          {/* Fleet Owner Performance */}
          <div className="mt-4 md:mt-0 md:w-1/3">
            <h2 className="text-xl font-bold mb-8 text-gray-500">Fleet Owner Performance</h2>
            <div className="border rounded-md flex flex-col gap-4">
              {fleetOwnerPerformance?.topFive.map((performance: any) => (
                <PerformanceItem
                  key={performance.fleetOwnerId}
                  number={performance.rank}
                  completedRequests={performance.completedRequestsCount}
                  name={performance.name}
                  percentage={performance.changePercent}
                  value={performance.earningsCompact}
                  isPositive={performance.profitLoss === 'profit'}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
